import { useCallback, useEffect, useMemo, useState } from "react";

import { useDesktopPanelStore } from "@/stores/panelStore";
import {
  getShortcutBinding,
  loadKeyboardShortcuts,
  shortcutFromEvent,
} from "@/lib/keyboardShortcuts";
import {
  applyWorkspaceTemplate,
  getTemplateShortcutTargets,
} from "@/lib/templates";
import {
  canRedoDrawingAction,
  canUndoDrawingAction,
  cancelPendingDrawings,
  duplicateDrawing as bridgeDuplicateDrawing,
  removeDrawing as bridgeRemoveDrawing,
  redoLastDrawingAction,
  undoLastDrawingAction,
} from "@/lib/lytick/overlay-bridge";
import { useDrawingStore, type DrawingToolId } from "@/stores/drawingStore";
import { useReplayStore } from "@/stores/replayStore";
import { useUIStore } from "@/stores/uiStore";
import { useWorkspaceStore } from "@/stores/workspaceStore";

const DRAWING_SHORTCUTS: Record<string, DrawingToolId> = {
  h: "horizontalLine",
  t: "trendLine",
  r: "rectangle",
  f: "fibonacci",
  v: "verticalLine",
  n: "textAnnotation",
  c: "parallelChannel",
  q: "riskReward",
};


function isEditableTarget(target: EventTarget | null) {
  if (
    target instanceof HTMLInputElement ||
    target instanceof HTMLTextAreaElement ||
    target instanceof HTMLSelectElement
  ) {
    return true;
  }

  if (target instanceof HTMLElement) {
    return target.isContentEditable;
  }

  return false;
}

export function useKeyboardShortcuts() {
  const [shortcuts, setShortcuts] = useState(loadKeyboardShortcuts);
  const { addToast, closeDrawer, closeModal, openModal, openCommandPalette } = useUIStore();
  const { closeAllPanels } =
    useDesktopPanelStore();
  const closeShortcutsOverlay = useWorkspaceStore(
    (state) => state.closeShortcutsOverlay,
  );
  const toggleShortcutsOverlay = useWorkspaceStore(
    (state) => state.toggleShortcutsOverlay,
  );
  const shortcutsOverlayOpen = useWorkspaceStore(
    (state) => state.shortcutsOverlayOpen,
  );
  const jumpToLatest = useCallback(() => {
    const jumpToLive = (
      window as unknown as {
        __LYTICK_JUMP_TO_LIVE__?: () => void;
      }
    ).__LYTICK_JUMP_TO_LIVE__;

    if (jumpToLive) {
      jumpToLive();
      return;
    }

    const chart = (window as Record<string, any>).__LYTICK_CHART__;
    if (!chart) {
      return;
    }

    try {
      chart.scrollToRealTime();
    } catch {
      // Ignore chart API differences across klinecharts builds.
    }
  }, []);

  const shortcutMap = useMemo(() => {
    return new Map(shortcuts.map((shortcut) => [shortcut.id, getShortcutBinding(shortcut)]));
  }, [shortcuts]);

  useEffect(() => {
    const handleShortcutUpdate = () => {
      setShortcuts(loadKeyboardShortcuts());
    };

    window.addEventListener("lytick:shortcuts-updated", handleShortcutUpdate);
    window.addEventListener("storage", handleShortcutUpdate);
    return () => {
      window.removeEventListener("lytick:shortcuts-updated", handleShortcutUpdate);
      window.removeEventListener("storage", handleShortcutUpdate);
    };
  }, []);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const shortcut = shortcutFromEvent(event);
      const target = event.target as HTMLElement;
      const isInputTarget = isEditableTarget(target);
      const uiState = useUIStore.getState();
      const panelState = useDesktopPanelStore.getState();
      const replayStore = useReplayStore.getState();
      const drawingStore = useDrawingStore.getState();

      const matchesShortcut = (shortcutId: string) =>
        shortcutMap.get(shortcutId) === shortcut;

      if (matchesShortcut("command-palette")) {
        event.preventDefault();
        openCommandPalette();
        return;
      }

      if (matchesShortcut("show-shortcuts")) {
        event.preventDefault();
        toggleShortcutsOverlay();
        return;
      }

      if (shortcutsOverlayOpen) {
        if (event.key === "Escape") {
          event.preventDefault();
          closeShortcutsOverlay();
        }
        return;
      }

      if (isInputTarget) {
        if (event.key === "Escape") {
          target.blur();
          event.preventDefault();
        }
        return;
      }

      const hasOpenUI = uiState.activeModal !== null || uiState.activeDrawer !== null;
      const hasOpenPanels =
        uiState.isSettingsOpen || panelState.indicatorsOpen;

      if (matchesShortcut("close-panels")) {
        event.preventDefault();
        cancelPendingDrawings();
        closeModal();
        closeDrawer();
        closeAllPanels();
        useUIStore.getState().closeSettings();
        closeShortcutsOverlay();
        drawingStore.setActiveTool(null);
        drawingStore.clearDrawingSelection();
        return;
      }

      if (matchesShortcut("save-template")) {
        event.preventDefault();
        window.dispatchEvent(new CustomEvent("lytick:save-chart-template"));
        return;
      }

      if (matchesShortcut("zoom-in") || matchesShortcut("zoom-out")) {
        event.preventDefault();
        const chart = (window as Record<string, any>).__LYTICK_CHART__;

        if (!chart) {
          return;
        }

        try {
          chart.zoomAtCoordinate(matchesShortcut("zoom-in") ? 0.8 : 1.2);
        } catch {
          // Ignore chart API differences across klinecharts builds.
        }
        return;
      }

      if (matchesShortcut("switch-template-1") || matchesShortcut("switch-template-2") || matchesShortcut("switch-template-3")) {
        event.preventDefault();
        const targets = getTemplateShortcutTargets();
        const index = matchesShortcut("switch-template-1")
          ? 0
          : matchesShortcut("switch-template-2")
            ? 1
            : 2;
        const template = targets[index];

        if (template) {
          applyWorkspaceTemplate(template);
        }
        return;
      }

      const isCtrl = event.ctrlKey || event.metaKey;
      const isAlt = event.altKey;
      const isShift = event.shiftKey;

      if (isCtrl && !isAlt && !isShift && event.key === "s") {
        event.preventDefault();
        window.dispatchEvent(new CustomEvent("lytick:save-chart-template"));
        return;
      }

      if (isCtrl && !isAlt && !isShift && event.key === "z") {
        if (canUndoDrawingAction()) {
          event.preventDefault();
          undoLastDrawingAction();
        }
        return;
      }

      if (
        (isCtrl && isShift && !isAlt && event.key.toLowerCase() === "z") ||
        (isCtrl && !isShift && !isAlt && event.key.toLowerCase() === "y")
      ) {
        if (canRedoDrawingAction()) {
          event.preventDefault();
          redoLastDrawingAction();
        }
        return;
      }

      if (isCtrl && !isAlt && event.key === "a") {
        if (drawingStore.drawings.length > 0) {
          event.preventDefault();
          drawingStore.selectAllDrawings();
        }
        return;
      }

      if (isCtrl && isShift && !isAlt && event.key === "A") {
        if (
          drawingStore.selectedDrawingIds.length > 0 ||
          drawingStore.selectedDrawingId
        ) {
          event.preventDefault();
          drawingStore.clearDrawingSelection();
        }
        return;
      }

      if (isCtrl && isShift && (event.key === ">" || event.key === ".")) {
        event.preventDefault();
        jumpToLatest();
        return;
      }

      if (isCtrl && !isAlt && !isShift && event.key.toLowerCase() === "d") {
        const selectedIds =
          drawingStore.selectedDrawingIds.length > 0
            ? drawingStore.selectedDrawingIds
            : drawingStore.selectedDrawingId
              ? [drawingStore.selectedDrawingId]
              : [];
        if (selectedIds.length > 0) {
          event.preventDefault();
          selectedIds.forEach((id) => {
            bridgeDuplicateDrawing(id);
          });
        }
        return;
      }

      if (isCtrl && !isAlt && !isShift && (event.key === "ArrowLeft" || event.key === "ArrowRight")) {
        const chart = (window as Record<string, any>).__LYTICK_CHART__;

        if (!chart) {
          return;
        }

        event.preventDefault();

        try {
          chart.scrollByDistance((event.key === "ArrowLeft" ? -1 : 1) * 400);
        } catch {
          // Ignore chart API differences across klinecharts builds.
        }
        return;
      }

      if (isAlt && !isCtrl && !isShift) {
        const tool = DRAWING_SHORTCUTS[event.key.toLowerCase()];

        if (tool) {
          event.preventDefault();
          drawingStore.setActiveTool(drawingStore.activeTool === tool ? null : tool);
          return;
        }
      }

      if (event.key === " ") {
        event.preventDefault();

        if (replayStore.isActive) {
          replayStore.togglePause();
        }
        return;
      }

      if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
        event.preventDefault();
        const direction = event.key === "ArrowLeft" ? -1 : 1;
        const chart = (window as Record<string, any>).__LYTICK_CHART__;

        if (replayStore.isActive && replayStore.isPaused && chart) {
          try {
            const range = chart.getVisibleRange();
            const dataLength = chart.getDataList().length;
            const latestInView = range.to >= dataLength - 2;

            if (latestInView) {
              if (direction > 0) {
                replayStore.stepForward();
              } else {
                replayStore.stepBackward();
              }
            } else {
              chart.scrollByDistance(direction * 150);
            }
          } catch {
            if (direction > 0) {
              replayStore.stepForward();
            } else {
              replayStore.stepBackward();
            }
          }
        } else if (chart) {
          try {
            chart.scrollByDistance(direction * 150);
          } catch {
            // Ignore chart API differences across klinecharts builds.
          }
        }
        return;
      }

      if (event.key === "ArrowUp" || event.key === "ArrowDown") {
        event.preventDefault();
        const chart = (window as Record<string, any>).__LYTICK_CHART__;

        if (!chart) {
          return;
        }

        try {
          chart.zoomAtCoordinate(event.key === "ArrowUp" ? 0.8 : 1.2);
        } catch {
          // Ignore chart API differences across klinecharts builds.
        }
        return;
      }

      if (event.key === "Delete" || event.key === "Backspace") {
        if (drawingStore.activeTool) {
          event.preventDefault();
          cancelPendingDrawings();
          drawingStore.setActiveTool(null);
          return;
        }

        const selectedIds =
          drawingStore.selectedDrawingIds.length > 0
            ? drawingStore.selectedDrawingIds
            : drawingStore.selectedDrawingId
              ? [drawingStore.selectedDrawingId]
              : [];

        if (selectedIds.length > 0) {
          event.preventDefault();
          selectedIds.forEach((id) => {
            bridgeRemoveDrawing(id);
          });
          drawingStore.clearDrawingSelection();
          return;
        }
      }

      if (event.key === ".") {
        if (!replayStore.isActive) {
          event.preventDefault();
          jumpToLatest();
        }
        return;
      }

      // Home = jump to oldest candle, End = jump to latest candle
      if (event.key === "Home" && !isCtrl && !isAlt) {
        event.preventDefault();
        const chart = (window as Record<string, any>).__LYTICK_CHART__;
        if (chart) {
          try {
            chart.scrollToDataIndex(0, 300);
          } catch {
            // Ignore chart API version differences
          }
        }
        return;
      }

      if (event.key === "End" && !isCtrl && !isAlt) {
        event.preventDefault();
        jumpToLatest();
        return;
      }

      if (!isCtrl && !isAlt && !isShift && /^[a-z]$/.test(event.key) && !hasOpenUI && !hasOpenPanels) {
        event.preventDefault();
        openModal("symbolSearch");
        return;
      }

      for (let i = 1; i <= 7; i++) {
        if (matchesShortcut(`timeframe-${i}`) && !hasOpenUI && !hasOpenPanels) {
          event.preventDefault();
          window.dispatchEvent(new CustomEvent("lytick:open-timeframe", { detail: `${i}` }));
          break;
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [
    addToast,
    closeAllPanels,
    closeDrawer,
    closeModal,
    closeShortcutsOverlay,
    jumpToLatest,
    openModal,
    shortcutMap,
    shortcutsOverlayOpen,
    toggleShortcutsOverlay,
  ]);
}
